import { MIN_N } from './stats'
import type { Signal } from '../../types/insights'

/**
 * Automatische signalen voor het Overzicht van Inzicht: wat Sarah deze week anders kan doen.
 * Puur: de server (en de demo) geeft de cijfers, deze regels beslissen wat er gemeld wordt.
 * Externe bronnen zonder koppeling geven `null`: dan geen signaal, nooit een vals "0".
 */

export const SIGNAL_RULES = {
  /** Zo vaak gezocht zonder resultaat. */
  searchMin: 5,
  /** Zoveel wegglijdende klanten. */
  slippingMin: 5,
  /** Pagina met zoveel vertoningen in Google… */
  ctrImpressions: 200,
  /** …en minder dan dit deel klikt. */
  ctrMax: 0.01,
  /** Bezoek op de dag na een post, als veelvoud van het gemiddelde. */
  spikeFactor: 2,
  /** No-show zoveel keer hoger dan de vorige periode. */
  noShowFactor: 2,
  /** Hoogstens zoveel signalen tegelijk. */
  max: 6,
} as const

export type SignalInput = {
  zeroResultSearches: { term: string; count: number }[] | null
  slippingCustomers: number
  revisitAfterWeeks: number
  lowStock: { productId: string; name: string; stock: number; sold30: number }[]
  lowCtrPages: { path: string; title: string; impressions: number; clicks: number; editTo: string | null }[] | null
  postSpikes: { postId: string; date: string; visits: number; average: number }[] | null
  failedPayments: number
  noShow: { current: { total: number; noShow: number }; previous: { total: number; noShow: number } }
}

const order: Record<Signal['severity'], number> = { 'let-op': 0, kans: 1 }

export function signals(input: SignalInput): Signal[] {
  const out: Signal[] = []
  const r = SIGNAL_RULES

  if (input.failedPayments > 0) {
    out.push({
      id: 'incasso',
      severity: 'let-op',
      text: `${input.failedPayments} ${input.failedPayments === 1 ? 'incasso is' : "incasso's zijn"} mislukt`,
      detail: 'De klant heeft een betaallink gekregen. Bel of app als het na een paar dagen nog openstaat.',
      action: { label: 'Bekijk klanten', to: '/klanten' },
    })
  }

  input.lowStock
    .filter((p) => p.sold30 > p.stock)
    .forEach((p) =>
      out.push({
        id: `voorraad:${p.productId}`,
        severity: 'let-op',
        text: `${p.name} bijna op in de webshop`,
        detail: `Nog ${p.stock}, verkocht in 30 dagen: ${p.sold30}.`,
        action: { label: 'Inkoop boeken', to: '/voorraad?tab=inkoop' },
      }),
    )

  const { current, previous } = input.noShow
  if (current.total >= MIN_N && previous.total >= MIN_N && previous.noShow > 0) {
    const now = current.noShow / current.total
    const before = previous.noShow / previous.total
    if (now >= before * r.noShowFactor) {
      out.push({
        id: 'no-show',
        severity: 'let-op',
        text: `Meer no-shows: ${current.noShow} van ${current.total} afspraken`,
        detail: `Vorige periode ${previous.noShow} van ${previous.total}. Staat de herinnering 24 uur vooraf aan?`,
        action: { label: 'Bekijk agenda', to: '/agenda' },
      })
    }
  }

  ;(input.zeroResultSearches ?? [])
    .filter((s) => s.count >= r.searchMin)
    .sort((a, b) => b.count - a.count)
    .slice(0, 2)
    .forEach((s) =>
      out.push({
        id: `zoekterm:${s.term}`,
        severity: 'kans',
        text: `"${s.term}" ${s.count}× gezocht, niets gevonden`,
        detail: 'Een product, behandeling of artikel hierover vult een vraag die er al is.',
        action: { label: 'Schrijf artikel', to: '/journal' },
      }),
    )

  if (input.slippingCustomers >= r.slippingMin) {
    out.push({
      id: 'wegglijdend',
      severity: 'kans',
      text: `${input.slippingCustomers} klanten al ${input.revisitAfterWeeks} weken of langer niet gezien`,
      detail: 'Een persoonlijke mail of een plek in de agenda haalt de meesten terug.',
      action: { label: 'Maak doelgroep', to: `/mail?tab=abonnees&doelgroep=terugzien&weken=${input.revisitAfterWeeks}` },
    })
  }

  ;(input.postSpikes ?? [])
    .filter((p) => p.average > 0 && p.visits >= p.average * r.spikeFactor)
    .sort((a, b) => b.visits - a.visits)
    .slice(0, 1)
    .forEach((p) =>
      out.push({
        id: `post:${p.postId}`,
        severity: 'kans',
        text: `Post van ${p.date} bracht ${p.visits} bezoekers via Instagram`,
        detail: `Normaal ${Math.round(p.average)} per dag. Wat werkte, kun je vaker doen.`,
        action: { label: 'Bekijk Instagram', to: '/inzicht?tab=bereik&bereik=instagram' },
      }),
    )

  ;(input.lowCtrPages ?? [])
    .filter((p) => p.impressions >= r.ctrImpressions && p.clicks / p.impressions < r.ctrMax)
    .sort((a, b) => b.impressions - a.impressions)
    .slice(0, 1)
    .forEach((p) =>
      out.push({
        id: `ctr:${p.path}`,
        severity: 'kans',
        text: `${p.title}: ${p.impressions}× getoond in Google, ${p.clicks}× geklikt`,
        detail: 'Een duidelijkere titel en beschrijving in de zoekresultaten helpt.',
        ...(p.editTo ? { action: { label: 'Verbeter titel', to: p.editTo } } : {}),
      }),
    )

  return out.sort((a, b) => order[a.severity] - order[b.severity]).slice(0, r.max)
}
